
/**
 * Get snapshot of image or current video frame. If applicable, this function can also allow for zooming and panning.
 * @param {HTMLVideoElement|HTMLImageElement} media Video or image element.
 * @param {HTMLElement} [container] Parent element that media is rendered within. Only required if allowing for zooming/panning.
 * @param {object} [zoomTransformState] Transform state provided by react-zoom-pan-pinch. Only required if allowing for zooming/panning.
 * @param {number} [maxWidth] If given, the canvas is scaled down to be no wider than this (px). Use for previews/backgrounds: a full size frame can be
 *                            tens of MB of canvas memory, which adds up quickly on mobile browsers.
 * @returns {HTMLCanvasElement} Snapshot drawn on canvas element.
 */
export const getMediaSnapshot = (media, container, zoomTransformState = { positionX: 0, positionY: 0, scale: 1 }, maxWidth) => {
    // Get intrinsic dimensions of video/image
    // Intrinsic (source pixel) dimensions
    const intrinsicWidth = media.videoWidth ?? media.naturalWidth;
    const intrinsicHeight = media.videoHeight ?? media.naturalHeight;
    const intrinsicAspectRatio = intrinsicWidth / intrinsicHeight;

    // "Displayed" dimensions — when the source is being stretched to fill (~8:9 anamorphic
    // rendered at 16:9), snapshot should match what the user sees, not the natural aspect.
    const stretched = shouldStretchToContainer(intrinsicAspectRatio);
    const mediaHeight = intrinsicHeight;
    const mediaAspectRatio = stretched ? 16 / 9 : intrinsicAspectRatio;
    const mediaWidth = stretched ? mediaHeight * mediaAspectRatio : intrinsicWidth;

    /*
        We effectively have three different boxes to consider when calculating what part of image
        to download. From outermost to innermost:
            - Window: This is the static 16:9 rectangle we render the media in
            - Transform: This is the ReactZoomPanPinch 16:9 rectangle to which transformations are applied.
                         It is at least as large as the "Window" and entirely overlaps it.
            - Transformed Media: This is the image inside the "Transform". If the image is 16:9 it will
                                 be the same size as the "Transform". For other aspect ratios, it will be
                                 smaller and either match the width or the height.
    */

    // Get dimensions of the "Window"
    const windowWidth = container?.offsetWidth ?? mediaWidth;
    const windowHeight = container?.offsetHeight ?? mediaHeight;
    const windowAspectRatio = windowWidth / windowHeight;

    // Get px coordinates of the "Transform"
    // (0,0) is the top left of the "Window"
    const { positionX, positionY, scale } = zoomTransformState;
    const transformTopLeftX = positionX;
    const transformTopLeftY = positionY;
    const transformWidth = windowWidth * scale;
    const transformHeight = windowHeight * scale;

    // Get the coordinates of the "Transformed Media"
    let transformedMediaWidth, transformedMediaHeight, transformedMediaTopLeftX, transformedMediaTopLeftY;
    if (mediaAspectRatio >= windowAspectRatio) {
        // If media's aspect ratio is wider than 16:9, then "Transformed Media" will be full width of "Transform"
        transformedMediaWidth = transformWidth;
        transformedMediaHeight = transformedMediaWidth / mediaAspectRatio;
        transformedMediaTopLeftX = transformTopLeftX;
        // Media will be vertically centred
        transformedMediaTopLeftY = transformTopLeftY + (transformHeight - transformedMediaHeight) / 2;
    } else {
        // If media's aspect ratio is taller than 16:9, then "Transformed Media" will be full height of "Transform"
        transformedMediaHeight = transformHeight;
        transformedMediaWidth = transformedMediaHeight * mediaAspectRatio;
        transformedMediaTopLeftY = transformTopLeftY;
        // Media will be horizontally centred
        transformedMediaTopLeftX = transformTopLeftX + (transformWidth - transformedMediaWidth) / 2;
    }

    // Get the bounding edges of the overlap between the "Window" and the "Transformed Media"
    // This overlap is the cropped area we want to download
    // These are still in px coordinates with the origin being top left of the "Window"
    const boundingLeftX = Math.max(0, transformedMediaTopLeftX);
    const boundingRightX = Math.min(windowWidth, transformedMediaTopLeftX + transformedMediaWidth);
    const boundingTopY = Math.max(0, transformedMediaTopLeftY);
    const boundingBottomY = Math.min(windowHeight, transformedMediaTopLeftY + transformedMediaHeight);

    // Convert those bounding edges into px coordinates relative to the intrinsic size of the media
    // I.e. Each will be between (0,0) in top left and (mediaWidth, mediaHeight) in bottom right
    // We do this by calculating how far across/down the media the bounding edge lies as a fraction, and then multiplying by media width/height
    const boundingLeftXInMediaCoordinates = ((boundingLeftX - transformedMediaTopLeftX) / transformedMediaWidth) * mediaWidth;
    const boundingRightXInMediaCoordinates = ((boundingRightX - transformedMediaTopLeftX) / transformedMediaWidth) * mediaWidth;
    const boundingTopYInMediaCoordinates = ((boundingTopY - transformedMediaTopLeftY) / transformedMediaHeight) * mediaHeight;
    const boundingBottomYInMediaCoordinates = ((boundingBottomY - transformedMediaTopLeftY) / transformedMediaHeight) * mediaHeight;

    // Width and height of image file
    // Image will be cropped video frame
    const imageWidth = boundingRightXInMediaCoordinates - boundingLeftXInMediaCoordinates;
    const imageHeight = boundingBottomYInMediaCoordinates - boundingTopYInMediaCoordinates;

    // Create canvas element
    const outputScale = maxWidth && imageWidth > maxWidth ? maxWidth / imageWidth : 1;
    const outputWidth = Math.round(imageWidth * outputScale);
    const outputHeight = Math.round(imageHeight * outputScale);
    const canvas = document.createElement('canvas');
    canvas.width = outputScale === 1 ? imageWidth : outputWidth;
    canvas.height = outputScale === 1 ? imageHeight : outputHeight;
    const context = canvas.getContext('2d');

    // Draw cropped image on canvas. Source coords are in "displayed" media space, so map back
    // to intrinsic pixels for drawImage (a no-op unless the media is being stretched).
    const intrinsicScaleX = intrinsicWidth / mediaWidth;
    const intrinsicScaleY = intrinsicHeight / mediaHeight;
    context.drawImage(
        media,
        boundingLeftXInMediaCoordinates * intrinsicScaleX,
        boundingTopYInMediaCoordinates * intrinsicScaleY,
        imageWidth * intrinsicScaleX,
        imageHeight * intrinsicScaleY,
        0,
        0,
        // Fill the canvas, which is smaller than the cropped image when scaled down to maxWidth
        canvas.width,
        canvas.height
    );

    return canvas;
}

/**
 * Whether media with the given aspect ratio should be stretched to fill its 16:9 container.
 * Returns true for sources with a ~8:9 aspect ratio.
 * @param {number | undefined} aspectRatio width / height.
 * @returns {boolean}
 */
export const shouldStretchToContainer = (aspectRatio) => {
    if (!aspectRatio || !isFinite(aspectRatio)) {
        return false;
    }
    // 8:9 ~= 0.8889; tight tolerance so only true 8:9 sources trigger stretching
    return Math.abs(aspectRatio - 8 / 9) < 0.005;
};

/**
 * Video server path or URL for a period of streamable recorded video.
 * @param {string} uidd Camera uidd.
 * @param {number} startTime Timestamp of start of footage in ms.
 * @param {number} endTime Timestamp of end of footage in ms.
 * @param {string} cameraRecordedStreamName Stream name to use for recorded camera footage.
 * @param {string} token Auth token.
 * @param {object} [config] Additional config.
 * @param {string} [config.videoServer] URL of video server in appropriate region.
 * @param {('mp4'|'mpegts')} [config.format] Video type.
 * @param {boolean} [config.padding] Pad sections of time frame with no video.
 * @param {('h264', 'h265')} [config.codec] Transcode to the specified codec.
 * @returns {string} URL.
 */
export const getRecordedVideoUrl = (uidd, startTime, endTime, cameraRecordedStreamName, token, { videoServer, format = 'mp4', download = false, padding = false, codec = (canPlayH265 ? undefined : 'h264') } = {}) => {
    const [uid, deviceId] = uidd.split('.');

    // const queryString = ['inputStream=true', 'ignoreMvhd=true'];
    const queryString = ['frag=true'];
    if (padding) {
        queryString.push('padding=true');
    }
    if (download) {
        queryString.push('download=true');
    }
    if (codec && format === 'mpegts') {
        // Only request transcoding for mpegts
        // Need to change this if event playback ever uses /stream. Currently uses /video
        queryString.push(`vcodec=${codec}`);
    }
    queryString.push(`token=${token}`);

    return `${videoServer || ''}/stream/${format}/${uid}/${deviceId}/${startTime / 1000}/${endTime / 1000}/${cameraRecordedStreamName}${queryString.length > 0 ? `?${queryString.join('&')}` : ''}`;
}

/**
 * Does this device and browser support H265 codec?
 */
export let canPlayH265 = sessionStorage.canPlayH265 ? sessionStorage.canPlayH265 === 'true' : ['hvc1.1.6.L93.B0', 'hvc1.1.6.L120.90', 'hvc1.2.4.L120.90'].some((codec) => document.createElement('video').canPlayType(`video/mp4; codecs="${codec}"`) === 'probably');

// Once we successfully play a H265, set this to true which stops `canPlayH265` ever being set to false
let successfullyPlayedH265 = false;

/**
 * `canPlayH265` is initially an estimate. If we actually try to play an H265 video and it succeeds, use this function to ensure the flag can never be changed to `false`.
 */
export const reportH265Supported = () => {
    canPlayH265 = true;
    successfullyPlayedH265 = true;
}

/**
 * `canPlayH265` is initially an estimate. If we actually try to play an H265 video and it fails, use this function to set the flag to false.
 */
export const reportH265Unsupported = () => {
    if (!successfullyPlayedH265) {
        canPlayH265 = false;
    }
}